import type { CSSProperties, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { getInvertedColor } from '@elfsight/widget-utils';
import { ellipsisMixin } from '../mixins';

export interface BadgeProps extends PropsWithChildren {
  size?: 'small' | 'medium' | 'large';
  mode?: 'filled' | 'outline';
  borderMode?: 'rounded' | 'circular';
  color?: string;
  className?: string;
  style?: CSSProperties;
}
export const Badge = forwardRef<HTMLDivElement, BadgeProps>(function (
  {
    children,
    size = 'medium',
    mode = 'filled',
    borderMode = 'rounded',
    color = '#197BFF',
    ...forwardingProps
  },
  forwardRef
) {
  const textColor = mode === 'filled' ? getInvertedColor(color) : color;

  const fontStyle = {
    small: {
      fontSize: 9,
      lineHeight: 12
    },
    medium: {
      fontSize: 10,
      lineHeight: 13
    },
    large: {
      fontSize: 12,
      lineHeight: 16
    }
  }[size];

  const paddings = {
    small: { circular: '2px 7px', rounded: '2px 5px' },
    medium: { circular: '2px 7px', rounded: '2px 6px' },
    large: { circular: '2px 7px', rounded: '2px 6px' }
  }[size][borderMode];

  return (
    <StyledBadge
      ref={forwardRef}
      _fontStyle={fontStyle}
      _padding={paddings}
      _mode={mode}
      _borderMode={borderMode}
      _color={color}
      _textColor={textColor}
      {...forwardingProps}
    >
      {children}
    </StyledBadge>
  );
});

const StyledBadge = styled.div<{
  _fontStyle: {
    fontSize: number;
    lineHeight: number;
  };
  _mode: 'filled' | 'outline';
  _borderMode: 'rounded' | 'circular';
  _color: string;
  _textColor: string;
  _padding: string;
}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: ${({ _textColor }) => _textColor};
  font-size: ${({ _fontStyle }) => _fontStyle?.fontSize}px;
  line-height: ${({ _fontStyle }) => _fontStyle?.lineHeight}px;
  padding: ${({ _padding }) => _padding};

  border-radius: ${({ _borderMode }) =>
    _borderMode === 'rounded' ? '4px' : '100px'};

  background: ${({ _mode, _color }) => (_mode === 'filled' ? _color : `none`)};
  border: ${({ _mode, _color }) =>
    _mode === 'filled' ? 'none' : `1px solid ${_color}`};
  font-weight: bold;
  text-transform: uppercase;
  ${ellipsisMixin}
`;
